import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Session } from '@snapshots/domain';
import { Copies, Shell } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import { Outage, Problems } from '@snapshots/ui';
import { Callout, Loading, TechnicalDetails, Unavailable } from '@beyond-js/ui/react';
import { CaptureView } from './pages/capture';
import { Compare } from './pages/compare';
import { StorageView } from './pages/storage';
import { Notifications } from './pages/notifications';
import { Overview } from './pages/overview';
import { OperatorView } from './pages/operator';
import { SignIn } from './pages/signin';

/**
 * What is shown for each standing. "Still reading" is not "signed out", and an unreachable service is
 * neither: a sign-in link when the service is down would send a person somewhere that cannot answer.
 * An unreachable service tries again by itself on the family's schedule (`Unavailable`'s `again`), says
 * when it last tried and opens as usual once it answers, with no reload (D132). It is one read with nothing
 * else to show, so it is the family's outage form (D163): who did not answer, from the family's catalog of
 * causes (this service, or Beyond Accounts when the service could not check the session there), with the
 * read's evidence under Technical details.
 *
 * Reading and departing are one waiting element, which the document opening of `@beyond-js/ui` waits
 * on: its value names the step in the family's words, `session` while the session is read and `signin`
 * while the browser leaves for Beyond Accounts. It stays until the browser leaves, so the opening never
 * ends on a page that is about to go.
 */
export function App() {
	const { standing, warning, text, refresh, language, refusal } = usePresence();
	if (standing === 'reading' || standing === 'departing') {
		return (
			<div data-bui-opening-wait={standing === 'reading' ? 'session' : 'signin'} className="mx-auto max-w-md px-4 py-16">
				<Loading label={standing === 'reading' ? Loading.text({ name: text('loading.who'), kind: 'reading', language }) : text(`state.${standing}`)} />
			</div>
		);
	}
	if (standing === 'unavailable') {
		const cause = (refusal && Outage.cause(refusal)) || 'snapshots';
		const details = refusal ? { ...Problems.details(refusal), locale: language, labels: TechnicalDetails.labels[language] } : null;
		return (
			<div className="mx-auto flex max-w-md flex-col gap-4 px-4 py-16" data-outage={cause}>
				<Unavailable kind="outage" title={Outage.title(text, cause)} reason={text('state.unavailable.reason')} again={refresh} locale={language} labels={Copies.unavailable(language)} details={details} />
			</div>
		);
	}
	if (standing === 'out') {
		return (
			<Routes>
				<Route path="*" element={<SignIn />} />
			</Routes>
		);
	}
	return (
		<>
			{warning && (
				<div className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md" data-warning={warning}>
					<Callout tone="danger" live title={text(warning)} />
				</div>
			)}
			<Routes>
				<Route path={Session.RETURN} element={<Arrived />} />
				<Route element={<Shell />}>
					<Route index element={<Overview />} />
					<Route path="/snapshots/:id" element={<CaptureView />} />
					{/* The address before the vocabulary of E9, which links already sent still use */}
					<Route path="/captures/:id" element={<CaptureView />} />
					<Route path="/compare" element={<Compare />} />
					<Route path="/storage" element={<StorageView />} />
					<Route path="/notifications" element={<Notifications />} />
						<Route path="/operator" element={<OperatorView />} />
					<Route path="*" element={<Navigate to="/" replace />} />
				</Route>
			</Routes>
		</>
	);
}

/** A return page reached while signed in (a session another tab started): go where the sign-in was going */
function Arrived() {
	const { pathname, search } = useLocation();
	return <Navigate to={Session.destination(pathname, search)} replace />;
}
